import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { DatePicker, Tooltip } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import ProcoreLogo from "assets/images/Procore_Logo.svg";
import dayjs from "dayjs";
import { getFilterSummaryLabel } from "./procurementFilters";
import styles from "./FilterIconHeader.module.css";

// ---------------------------------------------------------------------------
// Condition options per type
// ---------------------------------------------------------------------------

const TEXT_CONDITIONS = [
  { value: "contains", label: "Contains" },
  { value: "notContains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "notEqual", label: "Not equal" },
  { value: "startsWith", label: "Starts with" },
  { value: "endsWith", label: "Ends with" },
  { value: "blank", label: "Blank" },
  { value: "notBlank", label: "Not blank" },
];

const NUMBER_CONDITIONS = [
  { value: "equals", label: "Equals" },
  { value: "notEqual", label: "Not equal" },
  { value: "lessThan", label: "Less than" },
  { value: "lessThanOrEqual", label: "Less than or equal" },
  { value: "greaterThan", label: "Greater than" },
  { value: "greaterThanOrEqual", label: "Greater than or equal" },
  { value: "inRange", label: "In range" },
  { value: "blank", label: "Blank" },
  { value: "notBlank", label: "Not blank" },
];

const DATE_CONDITIONS = [
  { value: "equals", label: "Equals" },
  { value: "notEqual", label: "Not equal" },
  { value: "lessThan", label: "Before" },
  { value: "greaterThan", label: "After" },
  { value: "inRange", label: "In range" },
  { value: "blank", label: "Blank" },
  { value: "notBlank", label: "Not blank" },
];

const NO_VALUE_TYPES = new Set(["blank", "notBlank"]);

// ---------------------------------------------------------------------------
// Filter form sub-components
// ---------------------------------------------------------------------------

function TextFilterForm({ draft, onChange }) {
  const noValue = NO_VALUE_TYPES.has(draft.type);
  return (
    <>
      <select
        className={styles.filterSelect}
        value={draft.type || "contains"}
        onChange={(e) =>
          onChange({ ...draft, type: e.target.value, value: "" })
        }
      >
        {TEXT_CONDITIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {!noValue && (
        <input
          autoFocus
          className={styles.filterInput}
          type="text"
          placeholder="Filter value..."
          value={draft.value || ""}
          onChange={(e) => onChange({ ...draft, value: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
        />
      )}
    </>
  );
}

function NumberFilterForm({ draft, onChange }) {
  const noValue = NO_VALUE_TYPES.has(draft.type);
  const isRange = draft.type === "inRange";
  return (
    <>
      <select
        className={styles.filterSelect}
        value={draft.type || "equals"}
        onChange={(e) =>
          onChange({
            ...draft,
            type: e.target.value,
            value: null,
            valueTo: null,
          })
        }
      >
        {NUMBER_CONDITIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {!noValue && (
        <input
          autoFocus
          className={styles.filterInput}
          type="number"
          placeholder={isRange ? "From..." : "Value..."}
          value={draft.value ?? ""}
          onChange={(e) =>
            onChange({
              ...draft,
              value: e.target.value === "" ? null : Number(e.target.value),
            })
          }
          onKeyDown={(e) => e.stopPropagation()}
        />
      )}
      {isRange && (
        <input
          className={styles.filterInput}
          type="number"
          placeholder="To..."
          value={draft.valueTo ?? ""}
          onChange={(e) =>
            onChange({
              ...draft,
              valueTo: e.target.value === "" ? null : Number(e.target.value),
            })
          }
          onKeyDown={(e) => e.stopPropagation()}
        />
      )}
    </>
  );
}

function DateInput({ autoFocus, value, onChange }) {
  const dateValue = value ? dayjs(value, "YYYY-MM-DD") : null;

  return (
    <div className={styles.datePickerWrap}>
      <DatePicker
        autoFocus={autoFocus}
        format="MM/DD/YYYY"
        placeholder="Select date..."
        value={dateValue}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={(date) => {
          const dateString = date ? date.format("YYYY-MM-DD") : null;
          onChange(dateString);
        }}
        getPopupContainer={(trigger) => trigger.parentElement}
      />
    </div>
  );
}

function DateFilterForm({ draft, onChange }) {
  const noValue = NO_VALUE_TYPES.has(draft.type);
  const isRange = draft.type === "inRange";

  return (
    <>
      <select
        className={styles.filterSelect}
        value={draft.type || "equals"}
        onChange={(e) =>
          onChange({
            ...draft,
            type: e.target.value,
            value: null,
            valueTo: null,
          })
        }
      >
        {DATE_CONDITIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {!noValue && (
        <DateInput
          autoFocus
          value={draft.value}
          onChange={(v) => onChange({ ...draft, value: v })}
        />
      )}

      {isRange && (
        <DateInput
          value={draft.valueTo}
          onChange={(v) => onChange({ ...draft, valueTo: v })}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Portal popover — renders into document.body to escape overflow:hidden
// ---------------------------------------------------------------------------

const PANEL_WIDTH = 240;
const PANEL_APPROX_HEIGHT = 160;
const PROCORE_SYNC_FIELDS = new Set([
  "specType",
  "specResponse",
  "responsibleContractor",
  "submittalIssued",
  "submittalReturned",
]);

function FilterPopover({
  anchorEl,
  filterType,
  currentFilter,
  onApply,
  onClear,
  onClose,
}) {
  const panelRef = useRef(null);
  const defaultType =
    filterType === "number"
      ? "equals"
      : filterType === "date"
        ? "equals"
        : "contains";

  const [draft, setDraft] = useState(() =>
    currentFilter ? { ...currentFilter } : { type: defaultType, value: "" },
  );
  const [posStyle, setPosStyle] = useState({ visibility: "hidden" });

  useEffect(() => {
    setDraft(
      currentFilter ? { ...currentFilter } : { type: defaultType, value: "" },
    );
  }, [currentFilter, defaultType]);

  useLayoutEffect(() => {
    if (!anchorEl) return;

    const rect = anchorEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom + window.scrollY + 4;
    const spaceBelow = viewportHeight - rect.bottom;
    if (spaceBelow < PANEL_APPROX_HEIGHT && rect.top > PANEL_APPROX_HEIGHT) {
      top = rect.top + window.scrollY - PANEL_APPROX_HEIGHT - 4;
    }

    let left = rect.left + window.scrollX;
    if (left + PANEL_WIDTH > viewportWidth + window.scrollX) {
      left = viewportWidth + window.scrollX - PANEL_WIDTH - 8;
    }

    setPosStyle({ top, left });
  }, [anchorEl]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        anchorEl &&
        !anchorEl.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown, true);
    return () =>
      document.removeEventListener("mousedown", handleMouseDown, true);
  }, [onClose, anchorEl]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey, true);
    return () => document.removeEventListener("keydown", handleKey, true);
  }, [onClose]);

  const handleApply = () => {
    const noVal = NO_VALUE_TYPES.has(draft.type);
    const hasValue =
      noVal ||
      (draft.value !== null && draft.value !== undefined && draft.value !== "");
    if (hasValue) onApply(draft);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleApply();
  };

  const content = (
    <div
      ref={panelRef}
      className={styles.popover}
      style={posStyle}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.popoverLabel}>Filter</div>

      {filterType === "text" && (
        <TextFilterForm draft={draft} onChange={setDraft} />
      )}
      {filterType === "number" && (
        <NumberFilterForm draft={draft} onChange={setDraft} />
      )}
      {filterType === "date" && (
        <DateFilterForm draft={draft} onChange={setDraft} />
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.btnApply} onClick={handleApply}>
          Apply
        </button>
        <button
          type="button"
          className={styles.btnClear}
          onClick={() => {
            onClear();
            onClose();
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

// ---------------------------------------------------------------------------
// FilterIconHeader — AG Grid headerComponent
// ---------------------------------------------------------------------------

export function FilterIconHeader({
  displayName,
  description,
  centered,
  sortState,
  onToggleSort,
  field,
  filterType,
  columnFilterModel,
  onSetColumnFilter,
}) {
  const isSortable = typeof onToggleSort === "function";
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);

  const currentFilter = columnFilterModel?.[field] ?? null;
  const isActive = !!currentFilter;
  const showsProcoreIndicator = PROCORE_SYNC_FIELDS.has(field);

  const handleIconClick = useCallback((e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  }, []);

  const handleProcoreIconClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleApply = useCallback(
    (cond) => {
      onSetColumnFilter?.(field, cond);
    },
    [field, onSetColumnFilter],
  );

  const handleClear = useCallback(() => {
    onSetColumnFilter?.(field, null);
  }, [field, onSetColumnFilter]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const activeSummary = isActive
    ? getFilterSummaryLabel(currentFilter, filterType)
    : null;

  return (
    <div
      className={`${styles.headerCell} ${centered ? styles.headerCellCentered : ""}`}
    >
      {/* Sortable label area */}
      <div
        className={`${styles.labelArea} ${isSortable ? styles.labelAreaSortable : ""} ${showsProcoreIndicator ? styles.labelAreaWithIndicator : ""}`}
        onClick={
          isSortable
            ? (e) => {
                e.stopPropagation();
                onToggleSort();
              }
            : undefined
        }
      >
        <Tooltip title={description || displayName} placement="top">
          <span
            className={`${styles.labelText} ${centered ? styles.labelTextCentered : ""}`}
          >
            {displayName}
          </span>
        </Tooltip>
        {showsProcoreIndicator && (
          <Tooltip
            title="This column is being pulled from Procore"
            placement="top"
          >
            <span
              className={styles.procoreBadge}
              onClick={handleProcoreIconClick}
              aria-label="This column is being pulled from Procore"
            >
              <img
                src={ProcoreLogo}
                alt="Procore"
                className={styles.procoreLogo}
              />
            </span>
          </Tooltip>
        )}
        {sortState && (
          <span className={styles.sortIndicator}>
            {sortState === "asc" ? "▲" : "▼"}
          </span>
        )}
      </div>

      {/* Filter icon button */}
      <Tooltip
        title={activeSummary ? `Filter: ${activeSummary}` : "Filter column"}
        placement="top"
      >
        <button
          ref={btnRef}
          type="button"
          className={`${styles.filterBtn} ${isActive ? styles.filterBtnActive : ""}`}
          onClick={handleIconClick}
        >
          <FilterOutlined style={{ fontSize: 11 }} />
          {isActive && <span className={styles.filterActiveDot} />}
        </button>
      </Tooltip>

      {/* Portal popover — rendered outside AG Grid's DOM hierarchy */}
      {open && (
        <FilterPopover
          anchorEl={btnRef.current}
          filterType={filterType || "text"}
          currentFilter={currentFilter}
          onApply={handleApply}
          onClear={handleClear}
          onClose={handleClose}
        />
      )}
    </div>
  );
}
